import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, Car, Search } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "", label: "Any vehicle class" },
  { value: "flagship-suv", label: "Flagship SUV" },
  { value: "luxury-suv", label: "Luxury SUV" },
  { value: "super-suv", label: "Super SUV" },
  { value: "flagship-sedan", label: "Flagship Sedan" },
  { value: "executive-sedan", label: "Executive Sedan" },
  { value: "van", label: "Sprinter / Van" },
];

/**
 * HeroSearchBar — big "Find Your Ride" widget for the homepage hero.
 * Collects pickup date/time + return date/time + vehicle class, then
 * navigates to /fleet with query params so the fleet page can filter and
 * the vehicle detail page can pre-fill the AvailabilityCalendar.
 */
export default function HeroSearchBar() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const twoDays = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    startDate: today,
    startTime: "10:00",
    endDate: twoDays,
    endTime: "10:00",
    category: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const search = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    params.set("mode", "rental");
    if (form.startDate) params.set("start", `${form.startDate}T${form.startTime || "10:00"}`);
    if (form.endDate) params.set("end", `${form.endDate}T${form.endTime || "10:00"}`);
    if (form.category) params.set("category", form.category);
    navigate(`/fleet?${params}`);
  };

  return (
    <form
      onSubmit={search}
      data-testid="hero-search"
      className="w-full max-w-5xl bg-black/60 backdrop-blur-xl rounded-3xl border border-white/12 p-5 sm:p-6 shadow-[0_30px_120px_rgba(0,0,0,0.55)]"
    >
      <div className="label-caps text-cream text-[10px] mb-4 tracking-widest inline-flex items-center gap-2">
        <Search size={12} /> Find your ride
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Field icon={Calendar} label="Pickup date">
          <input
            type="date"
            data-testid="hero-search-start-date"
            value={form.startDate}
            min={today}
            onChange={(e) => set("startDate", e.target.value)}
            className="hero-input"
          />
        </Field>
        <Field icon={Clock} label="Pickup time">
          <input
            type="time"
            data-testid="hero-search-start-time"
            value={form.startTime}
            onChange={(e) => set("startTime", e.target.value)}
            className="hero-input"
          />
        </Field>
        <Field icon={Calendar} label="Return date">
          <input
            type="date"
            data-testid="hero-search-end-date"
            value={form.endDate}
            min={form.startDate || today}
            onChange={(e) => set("endDate", e.target.value)}
            className="hero-input"
          />
        </Field>
        <Field icon={Clock} label="Return time">
          <input
            type="time"
            data-testid="hero-search-end-time"
            value={form.endTime}
            onChange={(e) => set("endTime", e.target.value)}
            className="hero-input"
          />
        </Field>
        <Field icon={Car} label="Vehicle class">
          <select
            data-testid="hero-search-category"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            className="hero-input"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
      </div>
      <button
        type="submit"
        data-testid="hero-search-submit"
        className="mt-4 w-full sm:w-auto btn-cream px-8 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2"
      >
        <Search size={14} /> Search available vehicles
      </button>
      <style>{`
        .hero-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 12px;
          padding: 10px 12px 10px 34px;
          font-size: 13px;
          color: #fff;
          outline: none;
          transition: border-color .15s ease;
          color-scheme: dark;
        }
        .hero-input:focus { border-color: #f4e4c1; }
      `}</style>
    </form>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="relative block">
      <div className="label-caps text-[9px] text-white/50 mb-1.5">{label}</div>
      <div className="relative">
        <Icon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/70 pointer-events-none z-10" />
        {children}
      </div>
    </label>
  );
}
